var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { createElement, useRef } from 'react';
import { CLOCK } from '../../controllers/util-level/clock.controller';
import { useOnMount } from '../../hooks/lifecycle.hooks';
import { useCreateResizeQueryWithRef } from '../../hooks/useCreateResizeQueryWithRef.hook';
import joinClassName from '../../utils/className.utils';
import { setCSSCustomProperty } from '../../utils/css.utils';
import { useProps, useStore } from '../../utils/mobx.utils';
import { createUTCMoment, YYYYMMDDHHmmss } from '../../utils/time.utils';
import BaseButton from '../BaseButton/BaseButton';
import './BaseCalendarDay.scss';
import BaseCalendarEventList from './BaseCalendarEventList';
function BaseCalendarDay(props) {
    var p = useProps(props);
    var _a = useCreateResizeQueryWithRef(), ref = _a.ref, query = _a.query;
    var headerRef = useRef(null);
    var s = useStore(function () { return ({
        get nowMoment() {
            return CLOCK.localNowMoment;
        },
        get calendar() {
            return p.state;
        },
        get calendarMode() {
            return s.calendar.mode;
        },
        get calendarEvents() {
            return __spreadArray(__spreadArray([], p.internalCalendarEvents, true), p.events || [], true);
        },
        get startOfDayMoment() {
            return moment(p.day).startOf('day');
        },
        get endOfDayMoment() {
            return moment(p.day).endOf('day');
        },
        get midDayMoment() {
            return moment(p.day).hour(12);
        },
        get isToday() {
            return s.startOfDayMoment.isSame(s.nowMoment, 'day');
        },
        get dayOfWeek() {
            return s.startOfDayMoment.weekday();
        },
        get isBeforeTimeframe() {
            return s.midDayMoment.isBefore(s.calendar.startDateMoment);
        },
        get isAfterTimeframe() {
            return s.midDayMoment.isAfter(s.calendar.endDateMoment);
        },
        get isOutsideOfTimeframe() {
            return s.isBeforeTimeframe || s.isAfterTimeframe;
        },
        get isInsideOfTimeframe() {
            return !s.isOutsideOfTimeframe;
        },
        get eventsInThisDay() {
            return s.calendarEvents.filter(function (e) { return e.startTime && createUTCMoment(e.startTime).local().isSame(s.midDayMoment, 'day'); });
        },
        get shouldShowEventEndTime() {
            return s.calendar.showEventEndTimes;
        },
        getDefaultNewEventTime: function () {
            var momentToUse = (s.isToday ? moment(p.day).hour(CLOCK.localNowMoment.hour()).add(3, 'hours').startOf('hour')
                : moment(p.day).hour(9).startOf('hour'));
            while (s.eventsInThisDay.find(function (e) { return moment(e.startTime).isSame(momentToUse, 'hour'); })) {
                momentToUse.add(1, 'hour');
            }
            while (!momentToUse.isSame(s.midDayMoment, 'date') || momentToUse.hour() > 21) {
                momentToUse.subtract(1, 'hour');
            }
            var result = momentToUse.utc().format(YYYYMMDDHHmmss);
            return result;
        },
        get createEvent() {
            return p.eventCreator ? function () {
                p.eventCreator && p.eventCreator(s.getDefaultNewEventTime(), p.internalCalendarEvents);
            } : null;
        },
        get shouldDisplayAsTimeline() {
            return p.state.mode === 'week' && p.state.shouldDisplayAsTimeline;
        },
        get shouldShowWeekday() {
            return s.shouldDisplayAsTimeline;
        }
    }); });
    useOnMount(function () {
        if (p.index !== 0)
            return;
        var handler = function () {
            var _a, _b;
            var height = (_a = headerRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight;
            setCSSCustomProperty('--BaseCalendarDayHeaderHeight', height + 'px', (_b = ref.current) === null || _b === void 0 ? void 0 : _b.parentElement);
        };
        query.onResize(handler);
        handler();
    });
    return React.createElement(Observer, { children: function () { return (createElement(s.shouldDisplayAsTimeline ? 'div' : 'td', {
            className: joinClassName('BaseCalendarDay', s.isToday && 'today', s.isOutsideOfTimeframe && 'outsideOfTimeframe'),
            'data-day-of-week': s.dayOfWeek,
            ref: ref,
        }, React.createElement("div", { className: "BaseCalendarDayInner" },
            React.createElement("header", { className: "BaseCalendarDayHeader", ref: headerRef },
                s.shouldShowWeekday && React.createElement("p", { className: "BaseCalendarDayWeekday" }, s.startOfDayMoment.local().format('dd')[0]),
                React.createElement("h3", { className: "BaseCalendarDayTitle" }, s.startOfDayMoment.local().format('D'))),
            React.createElement(BaseCalendarEventList, __assign({}, p, { events: s.eventsInThisDay, eventTypeGroups: p.state.eventTypeGroups, shouldShowEventEndTime: s.shouldShowEventEndTime })),
            s.createEvent && React.createElement("span", { className: 'BaseCalendarDayBackgroundAddButton', onClick: s.createEvent }),
            s.createEvent && React.createElement(BaseButton, { className: "BaseCalendarDayAddButton subtle", onClick: s.createEvent, size: "sm" }, "+ New")))); } });
}
export default BaseCalendarDay;
